import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FaUser, 
  FaEdit, 
  FaTimes,
  FaSave,
  FaEnvelope,
  FaIdCard,
  FaUserShield
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const UserProfileContainer = styled.div`
  padding: 2rem;
  background: #f8f9fa;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #667eea;
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #e0e0e0;
  max-width: 600px;
  margin: 0 auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #333;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
  
  &:disabled {
    background-color: #f8f9fa;
    color: #666;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: all 0.2s;
  
  &.edit {
    background: #007bff;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  }
  
  &.save {
    background: #28a745;
    color: white;
    
    &:hover {
      background: #218838;
    }
  }
  
  &.cancel {
    background: #6c757d;
    color: white;
    
    &:hover {
      background: #5a6268;
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  
  &.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
  
  &.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const UserProfile = ({ onClose }) => {
  const { user, updateCurrentUser, updateCurrentUserPassword, error, loading } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    email: user?.email || '',
    username: user?.username || '',
    full_name: user?.full_name || ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      email: user?.email || '',
      username: user?.username || '',
      full_name: user?.full_name || ''
    });
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setShowPasswordSection(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Update profile data
      await updateCurrentUser(formData);
      
      // Update password if password section is active and passwords are provided
      if (showPasswordSection && passwordData.new_password) {
        if (passwordData.new_password !== passwordData.confirm_password) {
          alert('New passwords do not match');
          return;
        }
        if (passwordData.new_password.length < 6) {
          alert('New password must be at least 6 characters long');
          return;
        }
        
        await updateCurrentUserPassword({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        });
      }
      
      setIsEditing(false);
      setShowPasswordSection(false);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  const handleClose = () => {
    if (isEditing) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        setIsEditing(false);
        setShowPasswordSection(false);
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <UserProfileContainer>
      <Header>
        <Title>
          <FaUser />
          My Profile
        </Title>
        <CloseButton onClick={handleClose}>
          <FaTimes />
        </CloseButton>
      </Header>

      {error && <Alert className="error">{error}</Alert>}

      <ProfileCard>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              <FaEnvelope />
              Email
            </Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              disabled={!isEditing}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <FaIdCard />
              Username
            </Label>
            <Input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({...formData, username: e.target.value})}
              disabled={!isEditing}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <FaUser />
              Full Name
            </Label>
            <Input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({...formData, full_name: e.target.value})}
              disabled={!isEditing}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <FaUserShield />
              Role
            </Label>
            <Input
              type="text"
              value={user?.is_superuser ? 'Superuser' : 'Regular User'}
              disabled
            />
          </FormGroup>

          <FormGroup>
            <Label>Account Created</Label>
            <Input
              type="text"
              value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              disabled
            />
          </FormGroup>

          <FormGroup>
            <Label>Account Status</Label>
            <Input
              type="text"
              value={user?.is_active ? 'Active' : 'Inactive'}
              disabled
            />
          </FormGroup>

          {isEditing && (
            <>
              <div style={{ 
                borderTop: '1px solid #e0e0e0', 
                marginTop: '1rem', 
                paddingTop: '1rem' 
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '1rem' 
                }}>
                  <Label style={{ margin: 0 }}>
                    <FaUserShield />
                    Password Change
                  </Label>
                  <Button 
                    type="button" 
                    className="edit" 
                    onClick={() => setShowPasswordSection(!showPasswordSection)}
                    style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    {showPasswordSection ? 'Hide' : 'Change Password'}
                  </Button>
                </div>
                
                {showPasswordSection && (
                  <>
                    <FormGroup>
                      <Label>Current Password</Label>
                      <Input
                        type="password"
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                        placeholder="Enter your current password"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>New Password</Label>
                      <Input
                        type="password"
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                        placeholder="Enter new password (min 6 characters)"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Confirm New Password</Label>
                      <Input
                        type="password"
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                        placeholder="Confirm new password"
                      />
                    </FormGroup>
                  </>
                )}
              </div>
            </>
          )}

          <ButtonGroup>
            {!isEditing ? (
              <Button type="button" className="edit" onClick={handleEdit}>
                <FaEdit />
                Edit Profile
              </Button>
            ) : (
              <>
                <Button type="button" className="cancel" onClick={handleCancel}>
                  <FaTimes />
                  Cancel
                </Button>
                <Button type="submit" className="save" disabled={loading}>
                  {loading ? <LoadingSpinner /> : <FaSave />}
                  Save Changes
                </Button>
              </>
            )}
          </ButtonGroup>
        </Form>
      </ProfileCard>
    </UserProfileContainer>
  );
};

export default UserProfile; 